import React, { useContext, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import { useTranslation } from "react-i18next";
import {
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Collapse,
  Alert,
  useMediaQuery,
  Box,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Science as ScienceIcon,
  Translate as TranslateIcon,
  PermMedia as PermMediaIcon,
  Close as CloseIcon,
  DirectionsRun as DirectionsRunIcon,
  ExpandMore,
  ExpandLess,
  Menu,
  Dataset,
  ChevronLeft,
} from "@mui/icons-material";
import io from "socket.io-client";
import LanguageSelector from "./LanguageSelector";
import AccountMenu from "./AccountMenu";
import { logoutKeycloakUser } from "../../utils/auth";
import { fetchData } from "../../utils/api";
import { UserContext } from "../../App";
import RunStatusView from "./RunList";

export default function Sidebar() {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();
  // get url location
  const location = useLocation();
  const [showFailedRuns, setShowFailedRuns] = React.useState(false);
  const [showDemoNotification, setShowDemoNotification] = React.useState(true);
  const { datasets, experiments } = React.useContext(UserContext);
  const [runsOpen, setRunsOpen] = React.useState(true);
  const [mlflowRuns, setMlflowRuns] = React.useState([]); // State to store the  runs
  const { storedUid } = useContext(UserContext); // Fetch stored user ID
  const [scrollableHeight, setScrollableHeight] = React.useState("auto"); // Create refs for the header and account sections.
  const [accountOpen, setAccountOpen] = React.useState(false);
  const [openDatasets, setOpenDatasets] = React.useState({});
  const [openExperiments, setOpenExperiments] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasInitializedExpansion, setHasInitializedExpansion] =
    React.useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(() => {
    const storedValue = localStorage.getItem("drawerOpen");
    return storedValue === null ? true : storedValue === "true";
  });

  // Drawer Settings
  const drawerWidth = 350;
  const headerRef = React.useRef(null);
  const accountRef = React.useRef(null);
  const matches = !useMediaQuery("(max-width:1000px)");

  const openedMixin = () => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  });

  const closedMixin = () => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
      width: `calc(${theme.spacing(8)} + 1px)`,
    },
  });

  // Function to fetch the MLflow runs using the real API
  const fetchRuns = () => {
    if (datasets.length > 0 && experiments.length > 0) {
      setIsLoading(true);
      fetchData(`${process.env.REACT_APP_BACKEND_URL}/api/v0/runs-active`)
        .then((data) => {
          setMlflowRuns(data.runs);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const DrawerHeader = styled("div")(() => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  }));

  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
  })(({ open: drawerOpen }) => ({
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(drawerOpen && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": {
        width: drawerWidth,
        background: theme.palette.background.default,
        color: theme.palette.text.primary,
        transition: "width .2s ease-in-out",
      },
    }),
    ...(!drawerOpen && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
    "& .MuiDrawer-paper": {
      paddingBottom: theme.spacing(4),
      width: "inherit",
      overflow: "hidden",
      border: "none",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "100vh",
    },
  }));

  const isActiveRoute = (route) => {
    // simple method to check, whether a route is active
    const currentPath = location.pathname;
    // if route is a list, check if current path includes one of the routes
    if (Array.isArray(route)) {
      return route.some((r) => currentPath.includes(r));
    }
    return currentPath.includes(route);
  };

  const groupedRuns = mlflowRuns.reduce((acc, run) => {
    if (!acc[run.datasetName]) acc[run.datasetName] = [];
    acc[run.datasetName].push(run);
    return acc;
  }, {});

  const handleRunsToggle = () => {
    setRunsOpen(!runsOpen);
    if (!runsOpen && datasets.length > 0 && experiments.length > 0) {
      fetchRuns();
    }
  };

  const handleLogout = () => {
    logoutKeycloakUser();
  };

  const handleOpenAccount = () => {
    setAccountOpen(!accountOpen);
  };

  const handleToggleDataset = (datasetName) => {
    setOpenDatasets((prev) => ({
      ...prev,
      [datasetName]: !prev[datasetName],
    }));
  };

  const handleToggleExperiment = (datasetName, experimentName) => {
    setOpenExperiments((prev) => ({
      ...prev,
      [datasetName]: {
        ...prev[datasetName],
        [experimentName]: !prev[datasetName]?.[experimentName],
      },
    }));
  };

  const handleToggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  /* Styles */
  const listItemButtonStyles = {
    minHeight: 48,
    justifyContent: isDrawerOpen ? "initial" : "center",
    px: 2.5,
    mb: 1,
  };

  const listItemIconStyles = {
    minWidth: 0,
    mr: isDrawerOpen ? 2 : "auto",
    pl: 0,
    justifyContent: "center",
  };

  const ListItemTextStyles = {
    opacity: isDrawerOpen ? 1 : 0,
    color: "black",
  };

  const renderDrawerContent = () => (
    <>
      <List id="sidebar-container">
        <DrawerHeader ref={headerRef} sx={{ justifyContent: "center" }}>
          {isDrawerOpen && (
            <NavLink
              to="/"
              className="logoIcon"
              onClick={() => {
                if (!matches) handleToggleDrawer();
              }}
            >
              <img
                alt="Kilab.EE"
                src={theme.logo}
                style={{
                  maxHeight: "55px",
                  marginBottom: theme.spacing(2),
                }}
                className="logo"
              />
            </NavLink>
          )}
          {matches && (
            <IconButton>
              {isDrawerOpen ? (
                <Box
                  sx={{
                    display: "flex",
                    color: "black",
                    position: "absolute",
                    left: 40,
                    bottom: 30,
                  }}
                >
                  <ChevronLeft onClick={handleToggleDrawer} />
                </Box>
              ) : (
                <Menu onClick={handleToggleDrawer} sx={{ color: "black" }} />
              )}
            </IconButton>
          )}
        </DrawerHeader>
        <Divider />
        <ListItem
          key="demoDatasets"
          className="list-item"
          selected={isActiveRoute("examples")}
          disablePadding
        >
          <NavLink
            to="demo-datasets"
            onClick={() => {
              if (!matches) handleToggleDrawer();
            }}
          >
            <ListItemButton sx={listItemButtonStyles}>
              <ListItemIcon sx={listItemIconStyles}>
                <PermMediaIcon />
              </ListItemIcon>
              <ListItemText
                sx={ListItemTextStyles}
                primary={t("sidebar.UseCasesButton")}
              />
            </ListItemButton>
          </NavLink>
        </ListItem>

        <ListItem
          key="experimentAnalyzeHeader"
          className="list-item"
          selected={isActiveRoute([
            "quality-check",
            "inspect",
            "train",
            "analyze",
            "runs",
          ])}
          disablePadding
        >
          <NavLink
            to="quality-check"
            onClick={() => {
              if (!matches) handleToggleDrawer();
            }}
          >
            <ListItemButton sx={listItemButtonStyles}>
              <ListItemIcon sx={listItemIconStyles}>
                <ScienceIcon />
              </ListItemIcon>
              <ListItemText
                sx={ListItemTextStyles}
                primary={t("sidebar.CurrentExperimentButton")}
              />
            </ListItemButton>
          </NavLink>
        </ListItem>
        <ListItem
          key="Overview"
          className="list-item"
          selected={isActiveRoute("overview")}
          disablePadding
        >
          <NavLink
            to="overview"
            onClick={() => {
              if (!matches) handleToggleDrawer();
            }}
          >
            <ListItemButton sx={listItemButtonStyles}>
              <ListItemIcon sx={listItemIconStyles}>
                <Dataset />
              </ListItemIcon>
              <ListItemText
                sx={ListItemTextStyles}
                primary={t("sidebar.MyDatasetsButton")}
              />
            </ListItemButton>
          </NavLink>
        </ListItem>

        <ListItem
          key="ScheduledAndRunning"
          className="list-item"
          disablePadding
          endIcon={runsOpen ? <ExpandLess /> : <ExpandMore />}
          onClick={() => {
            if (!isDrawerOpen) setIsDrawerOpen(true);
            handleRunsToggle();
          }}
          sx={{
            backgroundColor: runsOpen ? "#f5f5f5" : "transparent",
            transition: "background-color 300ms ease-in-out",
          }}
        >
          <ListItemButton sx={listItemButtonStyles}>
            <ListItemIcon sx={listItemIconStyles}>
              <DirectionsRunIcon />
            </ListItemIcon>
            <ListItemText
              sx={ListItemTextStyles}
              primary={t("sidebar.ScheduledAndRunningButton")}
            />

            {runsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Box
          sx={{
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pt: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            /* ------------------------------------ScheduleAndRun Component Called--------------------------- */
            <RunStatusView
              t={t}
              runsOpen={runsOpen}
              scrollableHeight={scrollableHeight}
              groupedRuns={groupedRuns}
              showFailedRuns={showFailedRuns}
              setShowFailedRuns={setShowFailedRuns}
              openDatasets={openDatasets}
              openExperiments={openExperiments}
              handleToggleDataset={handleToggleDataset}
              handleToggleExperiment={handleToggleExperiment}
              matches={matches}
              handleToggleDrawer={handleToggleDrawer}
            />
          )}
        </Box>
      </List>

      {/* ------------------------------------Account Section--------------------------- */}
      <List
        ref={accountRef}
        sx={{
          // position: "absolute",
          bottom: "0px",
          // mb: 3,
        }}
      >
        {/* ------------------------------------Account Component Called--------------------------- */}
        <AccountMenu
          t={t}
          theme={theme}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          isActiveRoute={isActiveRoute}
          accountOpen={accountOpen}
          handleOpenAccount={handleOpenAccount}
          handleLogout={handleLogout}
          listItemButtonStyles={listItemButtonStyles}
          listItemIconStyles={listItemIconStyles}
          ListItemTextStyles={ListItemTextStyles}
        />
        {/* ----------------------------Language Section------------------------------- */}
        {/* <ListItem
          key="Language"
          className="list-item"
          onClick={handleOpenLanguage}
          sx={{
            padding: theme.spacing(0, 1),
          }}
        >
          <ListItemButton sx={listItemButtonStyles}>
            <ListItemIcon sx={listItemIconStyles}>
              <TranslateIcon />
            </ListItemIcon>
            <ListItemText
              sx={ListItemTextStyles}
              primary={t("sidebar.Language")}
            />
            
            <LanguageSelector isDrawerOpen={isDrawerOpen} />
          </ListItemButton>
        </ListItem> */}
        <ListItem
          key="Language"
          className="list-item"
          sx={{ padding: theme.spacing(0, 1) }}
        >
          <ListItemButton sx={listItemButtonStyles}>
            {isDrawerOpen && (
              <ListItemIcon sx={listItemIconStyles}>
                <TranslateIcon />
              </ListItemIcon>
            )}

            {isDrawerOpen && (
              <ListItemText
                sx={ListItemTextStyles}
                primary={t("sidebar.Language")}
              />
            )}

            <LanguageSelector
              isDrawerOpen={isDrawerOpen}
              listItemIconStyles={listItemIconStyles}
            />
          </ListItemButton>
        </ListItem>

        {isDrawerOpen && (
          <Grid
            container
            spacing={1}
            id="footer"
            justifyContent="center"
            alignItems="center"
          >
            <Grid>
              <Typography align="center">
                <Link to="about">{t("sidebar.AboutLink")}</Link>
              </Typography>
              <Typography align="center">
                <a href="https://www.zsw-bw.de/footer/datenschutz.html">
                  {" "}
                  {t("sidebar.PrivacyLink")}
                </a>{" "}
                &nbsp;
                <a href="https://www.zsw-bw.de/footer/impressum.html">
                  {" "}
                  {t("sidebar.ImprintLink")}{" "}
                </a>{" "}
                &nbsp;
                {t("sidebar.Copyright")}
              </Typography>
            </Grid>
          </Grid>
        )}
      </List>
    </>
  );

  useEffect(() => {
    // Establish a WebSocket connection for the user
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}`, {
      path: "/socket",
      forceNew: true,
      autoConnect: false,
      timeout: 50000,
      transports: ["websocket", "polling", "flashsocket"],
    });

    socket.connect();

    socket.on("connect", () => {
      // Join a general user room
      socket.emit("create-user-room", { uid: storedUid });

      // Listen for refresh signal
      socket.on("refresh-runs", () => {
        if (runsOpen) {
          fetchRuns();
        }
      });
    });

    return () => {
      // Cleanup WebSocket on component unmount
      socket.disconnect();
    };
  }, [storedUid, runsOpen]);
  useEffect(() => {
    const updateHeight = () => {
      const headerHeight = headerRef.current
        ? headerRef.current.offsetHeight
        : 0;
      const accountHeight = accountRef.current
        ? accountRef.current.offsetHeight
        : 0;
      // Subtracting an extra margin (e.g., 16px) if needed.
      const available = window.innerHeight - headerHeight - accountHeight - 300;
      setScrollableHeight(available);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    if (runsOpen && datasets.length > 0 && experiments.length > 0) {
      fetchRuns();
    }
  }, [runsOpen, datasets, experiments]);

  // Runs once when groupedRuns is ready
  useEffect(() => {
    if (
      !hasInitializedExpansion &&
      groupedRuns &&
      Object.keys(groupedRuns).length > 0
    ) {
      const newOpenDatasets = {};
      const newOpenExperiments = {};

      Object.entries(groupedRuns).forEach(([datasetName, runs]) => {
        newOpenDatasets[datasetName] = true;

        const experimentsMap = runs.reduce((acc, run) => {
          if (!acc[run.experimentName]) acc[run.experimentName] = [];
          acc[run.experimentName].push(run);
          return acc;
        }, {});

        newOpenExperiments[datasetName] = {};
        Object.keys(experimentsMap).forEach((expName) => {
          newOpenExperiments[datasetName][expName] = true;
        });
      });

      setOpenDatasets(newOpenDatasets);
      setOpenExperiments(newOpenExperiments);
      setHasInitializedExpansion(true); // prevent re-running
    }
  }, [groupedRuns, hasInitializedExpansion]);

  useEffect(() => {
    // Save to localStorage
    localStorage.setItem("drawerOpen", isDrawerOpen.toString());

    if (!isDrawerOpen) {
      setRunsOpen(false);
      setAccountOpen(false);
    } else {
      setRunsOpen(true);
    }
  }, [isDrawerOpen]);

  return (
    <>
      {process.env.REACT_APP_IS_DEMO === "true" ? (
        <Collapse in={showDemoNotification}>
          <Alert
            severity="info"
            action={
              <IconButton
                aria-label="close"
                onClick={() => {
                  setShowDemoNotification(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{
              mb: 12,
              position: "absolute",
              zIndex: 100,
              bottom: 0,
              right: 0,
            }}
          >
            {t("sidebar.DemoNotification")}
          </Alert>
        </Collapse>
      ) : (
        ""
      )}

      <div
        style={{
          height: "100vh",
        }}
      >
        {!matches ? (
          <>
            {/* ---------------------------------- Mobile View:-----------------------------------  */}
            <IconButton
              onClick={handleToggleDrawer}
              sx={{
                position: "fixed",
                top: 16,
                left: 16,
                zIndex: 1300,
                backgroundColor: "white",
                boxShadow: 1,
              }}
            >
              <Menu />
            </IconButton>

            <MuiDrawer
              className={`dock-open-${matches}`}
              anchor="left"
              open={isDrawerOpen}
              onClose={handleToggleDrawer}
              variant="temporary"
              ModalProps={{ keepMounted: true }}
            >
              {renderDrawerContent()}
            </MuiDrawer>
          </>
        ) : (
          /* ------------------------------------ Fullscreen View-----------------------------------------  */
          <Drawer
            variant="permanent"
            open={isDrawerOpen}
            className={`dock-open-${matches}`}
            sx={{
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100vh",
              paddingTop: 0,
            }}
          >
            {renderDrawerContent()}
          </Drawer>
        )}
      </div>
    </>
  );
}
